<script lang="ts">
import {defineComponent, type CSSProperties} from 'vue';
import GeneratedImage from '../GeneratedImage.vue';
import {buildPreviewRoute, buildDiffPreviewRoute} from './AllSpiceDiffViewRouteBuilder.ts';
import {transformForSnippet, SVG} from '../svg_utils.ts';
import {AllSpiceJsonLoader} from '../../features/allspicejson.ts';
import {Snippet} from '../../features/snippet.ts';
import {AllSpiceComponentRefs} from '../../features/allspicecomprefs.ts';
import type {ProjectData} from '../../allspice_app/types/project-data.ts';
import type {Container} from '../../container.ts';

const {assetUrlPrefix} = window.config;

export default defineComponent({
  name: 'AllSpiceDiffSnippetView',
  components: {GeneratedImage},
  inject: {
    untypedContainer: {from: 'container'},
  },
  props: {
    // True if displaying a diff between two commits, false if viewing a file.
    // When in view file mode, use the head parameters to specify commit, tree
    // path, etc.
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    repositoryOwner: {
      type: String,
      required: true,
      default: '',
    },
    repositoryName: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    baseTreePath: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    // Value is either three dots or two dots.
    diffType: {
      type: String,
      required: false,
      default: '...',
    },
    // Alt text for accessibility.
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // Space-separated SVG class(es): as-snippet, as-thumbnail.
    svgClassNames: {
      type: String,
      required: false,
      default: '',
    },
    // Layer IDs to display.
    layerIds: {
      type: String,
      required: false,
      default: null,
    },
    // Diff layers to show
    diffVisibility: {
      type: String,
      required: false,
      default: 'full',
    },
    docId: {
      type: String,
      required: false,
      default: '',
    },
    // Variant to show
    variant: {
      type: String,
      required: false,
      default: 'default',
    },
    dataAsViewCoords: {
      type: String,
      required: false,
      default: '',
    },
    // Width-to-height aspect ratio.
    dataAsAspectRatio: {
      type: String,
      required: false,
      default: '',
    },
    // Whether the snippet was taken when the PCB was flipped
    isFlipped: {
      type: Boolean,
      required: false,
      default: true,
    },
    // When true and loading the image errors, displays a default image.
    displayDefaultOnError: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      arePropsValid: true,
      isLoading: true,
      isLoadError: false,
      isHeadLoaded: false,
      isDiffFromLoaded: false,
      isDiffToLoaded: false,
      isDiffLoadError: false,
      showErrorDetails: false,
      errorDetailsText: '',
      showDiffFrom: true,
      showDiffTo: true,
      isStaleError: false,
    };
  },
  computed: {
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
    headPreviewUrl() {
      const url = buildPreviewRoute(this.container,
        this.repositoryOwner, this.repositoryName,
        this.headCommit, this.headTreePath);
      if (this.docId) {
        return `${url}&page_id=${this.docId}`;
      }
      return url;
    },
    diffFromPreviewUrl() {
      if (!this.baseTreePath) return '';
      // TODO: Use the base repo owner and base repo name.
      const url = buildDiffPreviewRoute(this.container,
        this.repositoryOwner, this.repositoryName,
        'from', this.headCommit, this.headTreePath,
        '', '', this.baseCommit, this.baseTreePath);
      if (this.docId) {
        return `${url}&page_id=${this.docId}`;
      }
      return url;
    },
    diffToPreviewUrl() {
      if (!this.headTreePath) return '';
      // TODO: Use the base repo owner and base repo name.
      const url = buildDiffPreviewRoute(this.container,
        this.repositoryOwner, this.repositoryName,
        'to', this.headCommit, this.headTreePath,
        '', '', this.baseCommit, this.baseTreePath);
      if (this.docId) {
        return `${url}&page_id=${this.docId}`;
      }
      return url;
    },
    isSnippet() {
      return (this.svgClassNames || '').split(' ').includes('as-snippet');
    },
    svgContainerClassNames() {
      // This should match setupSnippetContainer().
      const svgClasses = (this.svgClassNames || '').split(' ');
      const arr = [];
      if (svgClasses.includes('as-snippet')) {
        arr.push('as-snippet-container');
      }
      if (svgClasses.includes('as-thumbnail')) {
        arr.push('as-thumbnail-container');
      }
      return arr;
    },
    failedImageUrl() {
      return `${assetUrlPrefix}/img/failed.svg`;
    },
    isDiffLoading() {
      return this.isDiff &&
        !this.isDiffFromLoaded && !this.isDiffToLoaded &&
        !this.isDiffLoadError &&
        (this.showDiffTo || this.showDiffFrom);
    },
    headCssClass() {
      return {
        'svg-img': true,
        'head': true,
        'has-diff': this.isDiff,
        'is-snippet': this.isSnippet,
        'non-snippet': !this.isSnippet,
      };
    },
    diffCssClass() {
      return {
        'svg-img': true,
        'diff': true,
        // Don't show the diff parts before the full background.
        'hidden': !this.isHeadLoaded,
      };
    },
    cachePrefix() {
      return `${this.layerIds}:${this.variant}:${this.docId}`;
    },

    snippet() {
      return Snippet.parse(this.dataAsViewCoords, this.dataAsAspectRatio);
    },

    style(): CSSProperties {
      // Add 2px to account for 1px border
      return {
        visibility: 'visible',
        height: `${(this.snippet.height + 2).toFixed(1)}px`,
        width: `${(this.snippet.width + 2).toFixed(1)}px`,
      } as {
        height: string;
        width: string;
        visibility: 'visible'; // this makes the CSS typing happy
      };
    },
  },
  created() {
    if (!this.repositoryOwner || !this.repositoryName || !this.headTreePath || !this.headCommit) {
      console.error(
        'AllSpiceDiffSnippetView: Missing required props. ' +
        'Ensure that repositoryOwner, headTreePath, repositoryName, and headCommit are provided.',
      );
      this.arePropsValid = false;
      return;
    }

    if (this.diffVisibility === 'none') {
      this.showDiffTo = false;
      this.showDiffFrom = false;
    } else if (this.diffVisibility === 'added_changed') {
      this.showDiffTo = Boolean(this.headTreePath);
      this.showDiffFrom = false;
    } else if (this.diffVisibility === 'removed') {
      this.showDiffTo = false;
      this.showDiffFrom = Boolean(this.baseTreePath);
    } else { // "full"
      this.showDiffTo = Boolean(this.headTreePath);
      this.showDiffFrom = Boolean(this.baseTreePath);
    }
  },
  mounted() {
    if (!this.diffFromPreviewUrl) {
      this.onDiffFromLoad();
    }
    if (!this.diffToPreviewUrl) {
      this.onDiffToLoad();
    }
  },

  methods: {
    /**
     * @param {Response | undefined} resp
     */
    displayLoadError(resp?: Response | undefined) {
      console.error(`Head file could not load.`);
      this.isLoading = false;     // Disable loader
      this.isLoadError = true;
      if (resp?.status === 404) {
        this.isStaleError = true;
        return;
      }
      this.errorDetailsText = `Error: Failed to load file from server. Please contact maintainers@allspice.io if this issue persists.`;
      this.showErrorDetails = true;
    },

    /**
     * @param {Response | undefined} resp
     */
    displayDiffResponseError(resp: Response | undefined) {
      console.error(`Diff resp not found. Status code: ${resp?.status}`);
      if (resp?.status === 404) {
        this.isStaleError = true;
        return;
      }
      if (resp) {
        this.errorDetailsText = `Error: Failed to receive diff from server. Status code: ${resp.status}. Please contact maintainers@allspice.io if this issue persists.`;
      } else {
        this.errorDetailsText = `Error: Failed to receive diff from server. Please contact maintainers@allspice.io if this issue persists.`;
      }
      this.showErrorDetails = true;
    },

    displayUnknownError(err: unknown) {
      console.error(err);
      this.errorDetailsText = 'Error: Failed to load diff. Please contact maintainers@allspice.io if this issue persists.';
      this.showErrorDetails = true;
    },

    onHeadPreviewLoad() {
      this.isHeadLoaded = true;
      this.isLoading = false; // Disable loader
    },

    // All head failures route through displayLoadError; displayUnknownError speaks of the diff.
    onHeadPreviewError(fetchRetryerOrError: {response?: Response; errorDetails?: string}) {
      this.displayLoadError(fetchRetryerOrError?.response);
    },

    onDiffFromLoad() {
      this.isDiffFromLoaded = true;
    },

    onDiffToLoad() {
      this.isDiffToLoaded = true;
    },

    onDiffError(fetchRetryerOrError: {response?: Response; errorDetails?: string}) {
      this.isDiffLoadError = true;
      if (fetchRetryerOrError.response) {
        this.displayDiffResponseError(fetchRetryerOrError.response);
      } else if (fetchRetryerOrError.errorDetails) {
        this.displayUnknownError(fetchRetryerOrError.errorDetails);
      } else {
        this.displayUnknownError(fetchRetryerOrError);
      }
    },

    /**
     * For the working svg, we may need to resolve component references before transformations
     * @param {SVG} svg
     * @return {Promise<SVG>}
     */
    async onImagePreprocess(svg: SVG) {
      // Altium schematics may have component references that need to be resolved
      if (this.headTreePath.toLowerCase().endsWith('.schdoc')) {
        const url = AllSpiceJsonLoader.buildPrjUrl(this.container,
          this.repositoryOwner, this.repositoryName, this.headCommit, this.headTreePath);
        const results = await AllSpiceJsonLoader.load<ProjectData>(this.container, url, null);
        // map paths to urls
        const headUrls = results.headJson?.documents ? Object.fromEntries(
          Object.entries(results.headJson.documents).map(([docId, docPath]) =>
            [docId, [
              AllSpiceJsonLoader.buildUrl(this.container, this.repositoryOwner, this.repositoryName, this.headCommit, docPath),
              AllSpiceComponentRefs.buildSvgLinkUrl(this.container, this.repositoryOwner, this.repositoryName, this.headCommit, docPath),
            ]],
          )) : {};

        const el = await svg.svg();
        await AllSpiceComponentRefs.forSvg(this.container, el, headUrls);
        return this.onImageTransform(svg);
      }
      return this.onImageTransform(svg);
    },

    /**
     * @param {SVG} svgDoc
     * @return {SVG}
     */
    async onImageTransform(svgDoc: SVG) {
      let transformed = await transformForSnippet(svgDoc, {
        layerIds: this.layerIds,
        variant: this.variant,
        targetDocId: this.docId,
      });

      if (this.isFlipped) {
        const transformedEl = await transformed.svg();
        const layerGroup = transformedEl.querySelector('g:not([data-layer-id])');
        layerGroup.replaceChildren(...Array.from(layerGroup.children).reverse());

        transformed = SVG.fromElement(transformedEl);
      }

      return transformed;
    },
  },
});

</script>

<template>
  <div
    v-if="arePropsValid"
    class="svg-wrapper"
  >
    <div class="svg-schematic">
      <div
        v-if="!isLoadError"
        :class="svgContainerClassNames"
        :style="style"
      >
        <generated-image
          v-show="!isLoading"
          ref="headGeneratedImage"
          :image-class="headCssClass"
          :src="headPreviewUrl"
          :prefix="cachePrefix"
          :alt="alt"
          :image-transform="onImagePreprocess"
          :snippet="snippet"
          :is-flipped="isFlipped"
          @load="onHeadPreviewLoad"
          @error="onHeadPreviewError"
        />
        <generated-image
          v-if="isDiff && showDiffFrom"
          v-show="!isLoading"
          ref="diffFromGeneratedImage"
          :image-class="diffCssClass"
          :src="diffFromPreviewUrl"
          :prefix="cachePrefix"
          :image-transform="onImageTransform"
          :snippet="snippet"
          :is-flipped="isFlipped"
          @load="onDiffFromLoad"
          @error="onDiffError"
        />
        <generated-image
          v-if="isDiff && showDiffTo"
          v-show="!isLoading"
          ref="diffToGeneratedImage"
          :image-class="diffCssClass"
          :src="diffToPreviewUrl"
          :prefix="cachePrefix"
          :image-transform="onImageTransform"
          :snippet="snippet"
          :is-flipped="isFlipped"
          @load="onDiffToLoad"
          @error="onDiffError"
        />
        <div
          v-if="isLoading || isDiffLoading"
          class="loading-container"
        >
          <div class="is-loading" />
          LOADING design file
        </div>
      </div>
      <div v-if="!isLoading && isLoadError && !isStaleError">
        <img
          class="ui active centered inline text"
          :src="failedImageUrl"
        >
      </div>
      <div
        v-if="!isLoading && isLoadError && isStaleError"
        class="snippet-stale-placeholder"
        :style="style"
      />
    </div>
    <div
      v-if="showErrorDetails"
      class="ui negative message"
    >
      {{ errorDetailsText }}
    </div>
    <div
      v-if="isStaleError"
      class="snippet-stale-note"
    >
      Diff no longer available.
    </div>
  </div>
</template>

<style scoped>

.loading-container {
  z-index: 6;
  position: absolute;
  width: 100%;
  text-align: center;
}

.loading-container .is-loading {
  height: 80px;
}

.svg-wrapper {
  margin: 4px;
  position: relative;
}

.svg-schematic {
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  border: solid 1px #495057;
  width: 100%;
  max-height: 100%;
}

.svg-img {
  width: 100%;
  height: auto;
}

.svg-img.head.has-diff {
  filter: grayscale(0.9);
}

.svg-img.diff {
  position: absolute;
  top: 1px;
  left: 1px;
}

.svg-img.diff.hidden {
  visibility: hidden;
}

.snippet-stale-note {
  margin-top: 6px;
  max-width: var(--as-snippet-thumbnail-width);
  font-size: 0.9em;
  color: var(--color-text-light-2);
}

.snippet-stale-placeholder {
  background: var(--color-secondary-light-2);
  border-radius: 2px;
}

</style>
